<template>
  <KInputText
    ref="input"
    v-model="value"
    :id="id"
    :label="label"
    :name="name"
    :type="type"
    :disabled="disabled"
    :hide-label="hideLabel"
    :class="{ 'k-input-text--invalid': errorMessage }"
    :aria-describedby="errorMessage ? `${id}-error` : null"
  >
    <!-- Label-->
    <template #label>
      <label :class="['k-label', { 'sr-only': hideLabel }]" :for="id">
        {{ label }}
        <span v-if="requiredLabel" class="k-label--required">(required)</span>
        <span v-if="optionalLabel" class="k-label--optional">(optional)</span>
        <span v-if="subtitle" class="k-label--subtitle">{{ subtitle }}</span>
      </label>
    </template>

    <!-- Error message -->
    <template v-if="errorBag" #message>
      <small
        v-for="error in errors"
        class="k-input-text__message"
        :id="`${id}-error`"
        :key="`${id}-${error}`"
      >
        {{ error }}
      </small>
    </template>
    <template v-else-if="errorMessage" #message>
      <small class="k-input-text__message" :id="`${id}-error`">{{ errorMessage }}</small>
    </template>
  </KInputText>
</template>

<script setup lang="ts">
import { ref } from "vue";
import { KInputText } from "@k2united/frontend_components";
import { useField } from "vee-validate";

interface Props {
  /**
   * Same as HTML element ID
   */
  id: string;

  /**
   * Input label
   */
  label: string;

  /**
   * Required for VeeValidate
   */
  name: string;

  /**
   * Same as HTML element type
   */
  type?: "text" | "email" | "password";

  /**
   * Disables the input
   */
  disabled?: boolean;

  /**
   * Only show the label for screen readers
   */
  hideLabel?: boolean;

  /**
   * Indicate that the field is required in the label
   */
  requiredLabel?: boolean;

  /**
   * Indicate that the field is optional in the label
   */
  optionalLabel?: boolean;

  /**
   * Indicate whether or not to display all errors at once for a field
   */
  errorBag?: boolean;

  /**
   * Additional text to display under the label
   */
  subtitle?: string | null;
}

const props = withDefaults(defineProps<Props>(), {
  type: "text",
  disabled: false,
  hideLabel: false,
  requiredLabel: false,
  optionalLabel: false,
  subtitle: null,
  errorBag: false
});

const { value, errorMessage, errors } = useField<string>(() => props.name, undefined, {
  validateOnValueUpdate: false
});

const input = ref();

const focus = () => {
  if (input.value) {
    input.value.$el.children[1].children[0].focus();
  }
};

defineExpose({
  focus
});
</script>

<style scoped lang="scss">
:deep(.k-input-text--invalid) {
  @include border-danger;
}

.k-input-text__message {
  @include error-message;
}

.k-label--required,
.k-label--optional {
  @include body-x-small;
  color: $color-text-subtle;
}

.k-label--subtitle {
  color: $color-text-subtle;
  display: block;
  @include body-x-small;
}
</style>
